import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { AuthMethodDialogConstants } from "../../Core/Common/Constants";
import AuthMethodHeader from "../Common/Navigation/Header/AuthMethodHeader";
import { AppNotifier } from "../Common/Notification/AppNotifier";
import { AuthenticationMethod, SendMethodWindow, ValidationType } from "../../Core/Common/Enums";
import HelpTextLabel from "./parts/HelpTextLabel";
import AuthMethodSelect from "src/components/AuthMethod/parts/AuthMethodSelect";
import { setDefaultAuthMethod } from "src/actionCreators/composeActionCreators";
import { ApplicationState } from "src/store";
import QuestionAnswerAuthMethod from "./parts/QuestionAnswerAuthMethod";
import { fetchAuthenticationMethodSettings } from "src/actionCreators/adminSettingsActionCreators";
import { fetchUserSettings } from "src/actionCreators/userSettingsActionCreators";
import { IValidationMessage, validateAllRecipientsQnA } from "src/helper/Validations";
import { ToasterMessages } from "src/helper/ToasterMessages";

const AuthMethodDialog: React.FC = () => {
    const userSettingsStore = useSelector((state: ApplicationState) => state.userSettingsState);
    const [currentWindow, setCurrentWindow] = useState(SendMethodWindow.METHOD_SELECT);
    const [selectedMethod, setSelectedMethod] = useState<AuthenticationMethod>(
        userSettingsStore.userSettings.authenticationMethodSettings.defaultAuthenticationMethod
    );

    const recipients = useSelector((state: ApplicationState) => state.recipients);
    const [searchParams] = useSearchParams();

    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(fetchAuthenticationMethodSettings());
        dispatch(fetchUserSettings());
        setSelectedMethod(parseInt(searchParams.get("selectedAuthMethod")) as AuthenticationMethod);
        setCurrentWindow(parseInt(searchParams.get("windowType")) as SendMethodWindow);
    }, []);

    const handleAuthMethodClick = selectedMethod => {
        setSelectedMethod(selectedMethod);
    };

    const handleOnNextClick = () => {
        const alert: IValidationMessage = {
            validationType: ValidationType.VALID,
            messageProperty: "",
            message: "",
        };
        const message = {
            type: selectedMethod,
            payload: null,
        };
        switch (currentWindow) {
            case SendMethodWindow.METHOD_SELECT:
                if (selectedMethod === AuthenticationMethod.QUESTIONANSWER) {
                    setCurrentWindow(SendMethodWindow.QUESTION_ANSWER);
                } else {
                    Office.context.ui.messageParent(JSON.stringify(message));
                }
                break;
            case SendMethodWindow.QUESTION_ANSWER:
                if (!validateAllRecipientsQnA(recipients)) {
                    return;
                }
                message.payload = recipients;
                Office.context.ui.messageParent(JSON.stringify(message));
                break;
            default:
                Office.context.ui.messageParent("close");
        }
    };

    const handleOnBackClick = () => {
        switch (currentWindow) {
            case SendMethodWindow.METHOD_SELECT:
                Office.context.ui.messageParent("close");
                break;
            case SendMethodWindow.QUESTION_ANSWER:
                setCurrentWindow(SendMethodWindow.METHOD_SELECT);
                break;
        }
    };

    const handleOnSetDefaultClick = (event: any, authMethod: AuthenticationMethod) => {
        event.preventDefault();
        dispatch(
            setDefaultAuthMethod(authMethod, result => {
                dispatch(fetchAuthenticationMethodSettings());
                dispatch(fetchUserSettings());
                AppNotifier.Success(ToasterMessages.SUCCESS.CHANGE_DEFAULT_AUTH_METHOD);
            })
        );
    };

    return (
        <div className="AuthMethodDialog-wrapper">
            {currentWindow === SendMethodWindow.METHOD_SELECT ? (
                <>
                    <AuthMethodHeader title={AuthMethodDialogConstants.METHOD_SELECTION_HEADER_TITLE} />
                    <HelpTextLabel title={AuthMethodDialogConstants.SELECT_METHOD_HELP_TEXT} />
                    <AuthMethodSelect
                        activeAuthMethod={selectedMethod}
                        defaultAuthMethod={
                            userSettingsStore.userSettings.authenticationMethodSettings.defaultAuthenticationMethod
                        }
                        onAuthMethodClick={handleAuthMethodClick}
                        onSetDefaultClick={handleOnSetDefaultClick}
                    />
                </>
            ) : (
                <QuestionAnswerAuthMethod />
            )}

            <div className="footer-btns qna-footer">
                <button className="button-grey" onClick={handleOnBackClick}>
                    Back
                </button>
                <button className="button-primary-blue-2" onClick={handleOnNextClick}>
                    Next
                </button>
            </div>
        </div>
    );
};

export default AuthMethodDialog;
